import React from "react";
import {
  Calendar,
  Check,
  Copy,
  ExternalLink,
  MapPin,
  MessageCircle,
  Phone,
  Printer,
  Send,
  ShoppingBag,
  Store,
  Ticket,
  Truck,
  User,
  Wallet,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/lib/format";
import {
  getDeliveryAddress,
  getOrderAmounts,
  getStatusLabel,
  isDelivery,
} from "./orderHelpers";

function InfoItem({ icon: Icon, label, children }) {
  return (
    <div className="flex min-w-0 items-start gap-2 rounded-lg border border-border/60 bg-surface px-2.5 py-2">
      <Icon className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </p>
        <p className="truncate text-sm font-semibold text-foreground">{children}</p>
      </div>
    </div>
  );
}

/** Lista de adicionais de um item (produtos ou ingredientes) */
function ExtrasList({ title, items, withQuantity = false }) {
  if (!items?.length) return null;
  return (
    <div className="space-y-0.5">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </p>
      {items.map((extra, index) => {
        const quantity = Number(extra.qty) || 1;
        const price = (Number(extra.price) || 0) * (withQuantity ? quantity : 1);
        return (
          <div key={`${extra.id ?? extra.name}-${index}`} className="flex justify-between gap-2 text-xs">
            <span className="text-foreground">
              + {extra.name}
              {withQuantity && <span className="text-muted-foreground"> x{quantity}</span>}
            </span>
            {price > 0 && (
              <span className="whitespace-nowrap text-muted-foreground">{formatCurrency(price)}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Detalhes do pedido: cliente, entrega (com mapa), itens com adicionais e
 * observações, resumo de valores e ações de impressão/pagamento.
 */
const OrderDetailsDialog = ({
  open,
  onOpenChange,
  order,
  onPrint,
  onStorePrinter,
  onCopyText,
  onPayment,
  onWhatsApp,
}) => {
  if (!order) return null;

  const delivery = isDelivery(order);
  const address = delivery ? getDeliveryAddress(order) : null;
  const { subtotal, discount, discountPercentage, freight, total } = getOrderAmounts(order);
  const closed = order.status === "Finalizado" || order.status === "Cancelado";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto p-4 sm:p-6">
        <DialogHeader className="space-y-1 text-left">
          <DialogTitle className="flex flex-wrap items-center gap-2 font-display text-xl text-primary">
            <ShoppingBag className="h-5 w-5" />
            Pedido #{order.code}
            <span className="rounded-md bg-secondary px-2 py-0.5 text-xs font-bold text-primary">
              {getStatusLabel(order)}
            </span>
            {order.payed && (
              <span className="inline-flex items-center gap-1 rounded-md border border-brand-pistachio/40 bg-brand-pistachio/15 px-1.5 py-0.5 text-xs font-semibold text-primary">
                <Check className="h-3 w-3" />
                Pago
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="flex items-center gap-1 text-xs">
            <Calendar className="h-3.5 w-3.5" />
            {order.date_time || "Data não informada"}
          </DialogDescription>
        </DialogHeader>

        {/* Cliente, contato, pagamento e entrega */}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <InfoItem icon={User} label="Cliente">
            {order.client?.name || "Não informado"}
          </InfoItem>
          <InfoItem icon={Phone} label="Contato">
            {order.client?.contact ? (
              <button
                type="button"
                onClick={() => onWhatsApp(order)}
                className="inline-flex items-center gap-1 text-primary hover:underline"
                title="Abrir WhatsApp"
              >
                {order.client.contact}
                <MessageCircle className="h-3.5 w-3.5" />
              </button>
            ) : (
              "Não informado"
            )}
          </InfoItem>
          <InfoItem icon={Wallet} label="Pagamento">
            {order.pay_method || "Não informado"}
            <span className={cn("ml-1 text-xs", order.payed ? "text-primary" : "text-terra")}>
              ({order.payed ? "pago" : "a receber"})
            </span>
          </InfoItem>
          <InfoItem icon={delivery ? Truck : Store} label="Entrega">
            {delivery ? "Delivery" : order.delivery_type || "Retirada"}
          </InfoItem>
        </div>

        {/* Endereço de entrega */}
        {delivery && (
          <section className="space-y-2 rounded-xl border border-border bg-surface p-3">
            <div className="flex items-center justify-between gap-2">
              <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-primary">
                <MapPin className="h-4 w-4" />
                Endereço de entrega
              </p>
              {address.mapQuery && (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${address.mapQuery}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                >
                  Abrir no mapa
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
            {address.lines.length ? (
              address.lines.map((line) => (
                <p key={line} className="text-sm leading-snug text-foreground">
                  {line}
                </p>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">Endereço não informado</p>
            )}
            {address.mapQuery && (
              <div className="h-44 overflow-hidden rounded-lg border border-border md:h-56">
                <iframe
                  title={`Mapa da entrega do pedido ${order.code}`}
                  src={`https://www.google.com/maps?q=${address.mapQuery}&output=embed`}
                  className="h-full w-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            )}
          </section>
        )}

        {/* Itens */}
        <section className="space-y-2">
          <h3 className="font-display text-sm font-bold uppercase tracking-[0.12em] text-foreground">
            Itens do pedido
          </h3>
          {(order.products || []).length === 0 ? (
            <p className="rounded-lg border border-dashed px-3 py-4 text-center text-sm text-muted-foreground">
              Nenhum item neste pedido
            </p>
          ) : (
            <ul className="divide-y divide-border rounded-xl border bg-card">
              {order.products.map((item, index) => (
                <li key={item.id ?? index} className="space-y-1.5 px-3 py-2.5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-foreground">
                        <span className="text-primary">{item.quantity || 1}x</span>{" "}
                        {item.product?.name || "Produto"}
                      </p>
                      {item.product?.description && (
                        <p className="line-clamp-2 text-xs text-muted-foreground">
                          {item.product.description}
                        </p>
                      )}
                    </div>
                    <span className="whitespace-nowrap font-display text-sm font-bold text-primary">
                      {formatCurrency(item.total_value)}
                    </span>
                  </div>
                  <ExtrasList title="Produtos selecionados" items={item.selected_produtos} withQuantity />
                  <ExtrasList title="Produtos extras" items={item.selectedExtraProdutos} withQuantity />
                  <ExtrasList title="Ingredientes" items={item.selected_ingredients} />
                  <ExtrasList title="Ingredientes extras" items={item.selectedExtraIngredients} />
                  {item.observation && (
                    <p className="rounded-md border border-terra/20 bg-terra-soft px-2 py-1.5 text-xs text-terra">
                      <span className="font-bold">Obs.:</span> {item.observation}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Resumo de valores */}
        <section className="space-y-1 rounded-xl border border-border bg-surface p-3 text-sm">
          <div className="flex justify-between text-muted-foreground">
            <span>Subtotal dos itens</span>
            <span>{formatCurrency(subtotal)}</span>
          </div>
          {discount > 0 && (
            <div className="flex justify-between text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <Ticket className="h-3.5 w-3.5" />
                Cupom {order.coupon_code} ({discountPercentage.toLocaleString("pt-BR")}%)
              </span>
              <span>−{formatCurrency(discount)}</span>
            </div>
          )}
          {freight > 0 && (
            <div className="flex justify-between text-muted-foreground">
              <span>Taxa de entrega</span>
              <span>{formatCurrency(freight)}</span>
            </div>
          )}
          <div className="flex items-baseline justify-between border-t border-border pt-1.5">
            <span className="font-semibold text-foreground">Total</span>
            <span className="font-display text-xl font-extrabold text-primary">
              {formatCurrency(total)}
            </span>
          </div>
        </section>

        {/* Ações */}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <button
            type="button"
            onClick={() => onPrint(order)}
            className="col-span-2 flex h-10 items-center justify-center gap-1.5 rounded-lg bg-primary px-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-brand-dark sm:col-span-1"
          >
            <Printer className="h-4 w-4" />
            Imprimir
          </button>
          <button
            type="button"
            onClick={() => onStorePrinter(order)}
            className="flex h-10 items-center justify-center gap-1.5 rounded-lg border border-border bg-surface px-3 text-sm font-semibold text-primary transition-colors hover:bg-muted"
            title="Envia o pedido para o app de impressão da loja"
          >
            <Send className="h-4 w-4" />
            Loja
          </button>
          <button
            type="button"
            onClick={() => onCopyText(order)}
            className="flex h-10 items-center justify-center gap-1.5 rounded-lg border border-border bg-surface px-3 text-sm font-semibold text-primary transition-colors hover:bg-muted"
          >
            <Copy className="h-4 w-4" />
            Texto
          </button>
          {!closed && (
            <button
              type="button"
              onClick={() => onPayment(order)}
              className="col-span-2 flex h-10 items-center justify-center gap-1.5 rounded-lg border border-border bg-surface px-3 text-sm font-semibold text-primary transition-colors hover:bg-muted sm:col-span-1"
            >
              <Wallet className="h-4 w-4" />
              {order.payed ? "Pagamento" : "Receber"}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OrderDetailsDialog;
